/**
 * Shared circular header controls — back arrow and generic icon slots.
 * Used by `ScreenHeader` and screens with custom header actions.
 */
import React from 'react';
import { StyleSheet, View, type StyleProp, type ViewStyle } from 'react-native';
import Animated, {
  interpolate,
  interpolateColor,
  useAnimatedStyle,
  Extrapolation,
  type SharedValue,
} from 'react-native-reanimated';
import { Clickable2 } from './Clickable2';
import { SvgIcon } from './SvgIcon';
import { useThemeColors, appTheme } from '../../shared/theme';
import { leadingBackArrowIcon } from '../../shared/i18n/rtl';
import {
  HEADER_BACK_ICON_SIZE_INLINE,
  INLINE_BACK_SIZE,
  SCREEN_HEADER_ICON_SIZE,
  DEFAULT_HEADER_SCROLL_THRESHOLD,
  resolveHeaderBackButtonShellStyle,
  resolveChromeRevealInputRange,
  type HeaderBackButtonAppearance,
} from '../../shared/theme/styles/headerTokens';

export type { HeaderBackButtonAppearance };

export {
  INLINE_BACK_SIZE as HEADER_BACK_BUTTON_SIZE,
  SCREEN_HEADER_ICON_SIZE as HEADER_BACK_ICON_SIZE_STACK,
  HEADER_BACK_ICON_SIZE_INLINE,
};

type HeaderButtonShellProps = {
  style?: StyleProp<ViewStyle>;
  children?: React.ReactNode;
};

export type HeaderBackButtonProps = {
  onPress: () => void;
  accessibilityLabel: string;
  /** Stack default 24; inline 22. */
  iconSize?: number;
  /** `outline` = border ring at rest; `elevated` = filled surface (inline / overlays). */
  appearance?: HeaderBackButtonAppearance;
  /** Reanimated shell style — e.g. scroll-linked fill on stack headers. */
  shellStyle?: StyleProp<ViewStyle>;
  /** Wrap the shell for animated styles. Defaults to `Animated.View`. */
  Shell?: React.ComponentType<HeaderButtonShellProps>;
  disabled?: boolean;
  iconColor?: string;
};

export function HeaderBackButton({
  onPress,
  accessibilityLabel,
  iconSize = SCREEN_HEADER_ICON_SIZE,
  appearance = 'outline',
  shellStyle,
  Shell,
  disabled = false,
  iconColor,
}: HeaderBackButtonProps) {
  const colors = useThemeColors() ?? appTheme.colors;
  const shell_colors = resolveHeaderBackButtonShellStyle(colors, appearance);
  const ShellComponent = Shell ?? Animated.View;
  const resolved_icon_color = iconColor ?? (disabled ? colors.textLight : colors.text);

  return (
    <Clickable2
      onPress={onPress}
      disabled={disabled}
      accessibilityLabel={accessibilityLabel}
      accessibilityRole="button">
      <ShellComponent style={[styles.shell, shell_colors, shellStyle]}>
        <SvgIcon name={leadingBackArrowIcon()} size={iconSize} color={resolved_icon_color} />
      </ShellComponent>
    </Clickable2>
  );
}

export type HeaderIconButtonProps = {
  onPress: () => void;
  accessibilityLabel: string;
  children: React.ReactNode;
  appearance?: HeaderBackButtonAppearance;
  shellStyle?: StyleProp<ViewStyle>;
  disabled?: boolean;
  hitSlop?: number | { top: number; bottom: number; left: number; right: number };
  haptics?: boolean;
};

/** Circular bordered slot for header right/left custom actions (close, add, etc.). */
export function HeaderIconButton({
  onPress,
  accessibilityLabel,
  children,
  appearance = 'elevated',
  shellStyle,
  disabled = false,
  hitSlop,
  haptics = false,
}: HeaderIconButtonProps) {
  const colors = useThemeColors() ?? appTheme.colors;
  const shell_colors = resolveHeaderBackButtonShellStyle(colors, appearance);

  return (
    <Clickable2
      onPress={onPress}
      disabled={disabled}
      hitSlop={hitSlop}
      haptics={haptics}
      accessibilityLabel={accessibilityLabel}
      accessibilityRole="button">
      <Animated.View style={[styles.shell, shell_colors, shellStyle]}>
        <View style={styles.iconSlot}>{children}</View>
      </Animated.View>
    </Clickable2>
  );
}

/** Scroll-linked back-button fill for stack headers (`scrollY` from `useScreenHeaderScroll`). */
export function useHeaderBackScrollFillStyle(
  scroll_y: SharedValue<number> | undefined,
  scroll_threshold = DEFAULT_HEADER_SCROLL_THRESHOLD,
  surface_elevated?: string,
  border_subtle?: string,
) {
  const colors = useThemeColors() ?? appTheme.colors;
  const fill = surface_elevated ?? colors.surface;
  const border = border_subtle ?? colors.borderSubtle;
  const back_fill_transparent = `${fill}00`;
  const input_range = resolveChromeRevealInputRange(scroll_threshold);

  return useAnimatedStyle(() => {
    'worklet';
    if (scroll_y == null) {
      return {};
    }
    const progress = interpolate(scroll_y.value, input_range, [0, 0.55, 1], Extrapolation.CLAMP);
    return {
      backgroundColor: interpolateColor(progress, [0, 1], [back_fill_transparent, fill]),
      borderColor: interpolateColor(progress, [0, 1], [border, border]),
    };
  }, [scroll_y, scroll_threshold, back_fill_transparent, fill, border]);
}

const styles = StyleSheet.create({
  shell: {
    width: INLINE_BACK_SIZE,
    height: INLINE_BACK_SIZE,
    borderRadius: INLINE_BACK_SIZE / 2,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: StyleSheet.hairlineWidth,
    overflow: 'hidden',
  },
  iconSlot: {
    alignItems: 'center',
    justifyContent: 'center',
  },
});

export default HeaderBackButton;
